import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { RefreshCcw, CheckCircle2, XCircle, AlertTriangle, User, Database, Mail, Clock } from 'lucide-react';
import { getEmailRedirectUrl } from '@/utils/environmentRedirects';

interface AuthEvent {
  timestamp: string;
  event: string;
  session: any;
  user: any;
}

interface VerificationSession {
  session_id: string;
  type: string;
  user_email: string;
  label: string;
  start_timestamp: string;
  last_timestamp: string;
  success: boolean;
  step_count: number;
  data: any;
}

interface VerificationStep {
  id: string;
  session_id: string;
  step: string;
  status: string;
  timestamp: string;
  details: any;
}

export const AuthDiagnostics = () => {
  const [authEvents, setAuthEvents] = useState<AuthEvent[]>([]);
  const [verificationSessions, setVerificationSessions] = useState<VerificationSession[]>([]);
  const [verificationSteps, setVerificationSteps] = useState<VerificationStep[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [currentSession, setCurrentSession] = useState<any>(null);
  const [testEmail, setTestEmail] = useState('test-auth@example.com');
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [activeTab, setActiveTab] = useState('realtime');
  const { toast } = useToast();

  // Real-time auth monitoring
  useEffect(() => {
    let subscription: any = null;

    if (isMonitoring) {
      subscription = supabase.auth.onAuthStateChange((event, session) => {
        const authEvent: AuthEvent = {
          timestamp: new Date().toISOString(),
          event,
          session: session ? {
            access_token: session.access_token ? '***PRESENT***' : null,
            refresh_token: session.refresh_token ? '***PRESENT***' : null,
            expires_at: session.expires_at,
            token_type: session.token_type,
          } : null,
          user: session?.user ? {
            id: session.user.id,
            email: session.user.email,
            email_confirmed_at: session.user.email_confirmed_at,
            confirmed_at: session.user.confirmed_at,
            created_at: session.user.created_at,
            role: session.user.role,
          } : null
        };

        setAuthEvents(prev => [authEvent, ...prev.slice(0, 19)]); // Keep last 20 events
        
        if (session) {
          setCurrentSession(session);
          setCurrentUser(session.user);
        } else {
          setCurrentSession(null);
          setCurrentUser(null);
        }

        console.log('[AuthDiagnostics] Auth event captured:', authEvent);
      });
    }

    return () => {
      if (subscription?.subscription) {
        subscription.subscription.unsubscribe();
      }
    };
  }, [isMonitoring]);

  // Get current auth state
  useEffect(() => {
    const getCurrentState = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setCurrentSession(session);
        setCurrentUser(session?.user || null);
      } catch (error) {
        console.error('[AuthDiagnostics] Error getting current session:', error);
      }
    };

    getCurrentState();
  }, []);

  // Load verification sessions
  const loadVerificationSessions = async () => {
    try {
      const { data: sessions, error } = await supabase
        .from('auth_verification_sessions')
        .select('*')
        .order('start_timestamp', { ascending: false })
        .limit(20);

      if (error) {
        console.error('[AuthDiagnostics] Error loading verification sessions:', error);
        toast({
          variant: "destructive",
          title: "Error loading verification sessions",
          description: error.message
        });
      } else {
        setVerificationSessions(sessions || []);
      }
    } catch (error) {
      console.error('[AuthDiagnostics] Unexpected error loading verification sessions:', error);
    }
  };

  // Load verification steps for a session
  const loadVerificationSteps = async (sessionId?: string) => {
    try {
      let query = supabase
        .from('auth_verification_steps')
        .select('*')
        .order('timestamp', { ascending: false });

      if (sessionId) {
        query = query.eq('session_id', sessionId);
      }

      const { data: steps, error } = await query.limit(50);

      if (error) {
        console.error('[AuthDiagnostics] Error loading verification steps:', error);
      } else {
        setVerificationSteps(steps || []);
      }
    } catch (error) {
      console.error('[AuthDiagnostics] Unexpected error loading verification steps:', error);
    }
  };

  // Test auth flow
  const testAuthFlow = async () => {
    try {
      const sessionId = crypto.randomUUID();
      
      // Record test session start
      await supabase
        .from('auth_verification_sessions')
        .insert({
          session_id: sessionId,
          type: 'auth_diagnostics_test',
          user_email: testEmail,
          label: 'Auth Diagnostics Test',
          data: { test: true, timestamp: new Date().toISOString() }
        });

      // Record test step
      await supabase
        .from('auth_verification_steps')
        .insert({
          id: crypto.randomUUID(),
          session_id: sessionId,
          step: 'auth_test_initiated',
          status: 'info',
          details: { email: testEmail, test_type: 'signup_flow' }
        });

      // Attempt signup
      const { data, error } = await supabase.auth.signUp({
        email: testEmail,
        password: 'TestPassword123!',
        options: {
          emailRedirectTo: getEmailRedirectUrl('signup')
        }
      });

      // Record result
      await supabase
        .from('auth_verification_steps')
        .insert({
          id: crypto.randomUUID(),
          session_id: sessionId,
          step: 'signup_api_response',
          status: error ? 'error' : 'success',
          details: {
            success: !error,
            error: error?.message || null,
            user_id: data?.user?.id || null,
            email_sent: data?.user && !data.user.email_confirmed_at
          }
        });

      // Update session
      await supabase
        .from('auth_verification_sessions')
        .update({
          success: !error,
          last_timestamp: new Date().toISOString(),
          step_count: 2,
          data: {
            test: true,
            completed: true,
            result: error ? 'failed' : 'success',
            error: error?.message || null
          }
        })
        .eq('session_id', sessionId);

      toast({
        title: error ? "Auth test failed" : "Auth test completed",
        description: error ? error.message : "Test completed successfully. Check verification logs.",
        variant: error ? "destructive" : "default"
      });

      // Refresh data
      loadVerificationSessions();
      loadVerificationSteps();

    } catch (error: any) {
      console.error('[AuthDiagnostics] Test auth flow error:', error);
      toast({
        variant: "destructive",
        title: "Test failed",
        description: error.message
      });
    }
  };

  useEffect(() => {
    loadVerificationSessions();
    loadVerificationSteps();
  }, []);

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const getStatusColor = (status: string, success?: boolean) => {
    if (success === false) return 'destructive';
    if (status === 'error') return 'destructive';
    if (status === 'success') return 'default';
    if (status === 'warning') return 'secondary';
    return 'outline';
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Authentication Diagnostics
            </CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  loadVerificationSessions();
                  loadVerificationSteps();
                }}
              >
                <RefreshCcw className="h-4 w-4 mr-1" />
                Refresh
              </Button>
              <Button
                variant={isMonitoring ? "destructive" : "default"}
                size="sm"
                onClick={() => setIsMonitoring(!isMonitoring)}
              >
                {isMonitoring ? 'Stop Monitoring' : 'Start Monitoring'}
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="realtime">Real-time Events</TabsTrigger>
              <TabsTrigger value="sessions">Verification Sessions</TabsTrigger>
              <TabsTrigger value="steps">Verification Steps</TabsTrigger>
              <TabsTrigger value="test">Test Auth Flow</TabsTrigger>
              <TabsTrigger value="current">Current State</TabsTrigger>
            </TabsList>

            <TabsContent value="realtime">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  {isMonitoring ? (
                    <>
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      <span className="text-sm">Monitoring active - auth events will appear below</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4 text-gray-400" />
                      <span className="text-sm">Click "Start Monitoring" to capture real-time auth events</span>
                    </>
                  )}
                </div>

                <ScrollArea className="h-96">
                  {authEvents.length === 0 ? (
                    <div className="text-center text-gray-400 py-8">
                      No auth events captured yet
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {authEvents.map((event, index) => (
                        <Card key={index}>
                          <CardContent className="p-3">
                            <div className="flex justify-between items-start">
                              <div>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline">{event.event}</Badge>
                                  <span className="text-xs text-gray-500">
                                    {formatTimestamp(event.timestamp)}
                                  </span>
                                </div>
                                {event.user && (
                                  <div className="mt-1 text-sm">
                                    User: {event.user.email} (ID: {event.user.id.substring(0, 8)}...)
                                  </div>
                                )}
                              </div>
                              <div className="text-right text-xs">
                                {event.session ? (
                                  <Badge variant="default">Session Active</Badge>
                                ) : (
                                  <Badge variant="secondary">No Session</Badge>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
            </TabsContent>

            <TabsContent value="sessions">
              <ScrollArea className="h-96">
                {verificationSessions.length === 0 ? (
                  <div className="text-center text-gray-400 py-8">
                    No verification sessions found
                  </div>
                ) : (
                  <div className="space-y-2">
                    {verificationSessions.map((session) => (
                      <Card key={session.session_id}>
                        <CardContent className="p-3">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="flex items-center gap-2">
                                <Badge variant={getStatusColor('', session.success)}>
                                  {session.type}
                                </Badge>
                                <span className="text-sm font-medium">{session.label}</span>
                              </div>
                              <div className="text-sm text-gray-600 mt-1">
                                Email: {session.user_email}
                              </div>
                              <div className="text-xs text-gray-500">
                                Started: {formatTimestamp(session.start_timestamp)}
                              </div>
                            </div>
                            <div className="text-right">
                              <Badge variant="outline">{session.step_count} steps</Badge>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="ml-2"
                                onClick={() => {
                                  loadVerificationSteps(session.session_id);
                                  setActiveTab('steps');
                                }}
                              >
                                View Steps
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>

            <TabsContent value="steps">
              <ScrollArea className="h-96">
                {verificationSteps.length === 0 ? (
                  <div className="text-center text-gray-400 py-8">
                    No verification steps found
                  </div>
                ) : (
                  <div className="space-y-2">
                    {verificationSteps.map((step) => (
                      <Card key={step.id}>
                        <CardContent className="p-3">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="flex items-center gap-2">
                                <Badge variant={getStatusColor(step.status)}>
                                  {step.step}
                                </Badge>
                                <Badge variant="outline">{step.status}</Badge>
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {formatTimestamp(step.timestamp)}
                              </div>
                              {step.details && (
                                <details className="mt-2">
                                  <summary className="text-xs cursor-pointer text-blue-600">
                                    Show details
                                  </summary>
                                  <pre className="text-xs bg-gray-50 p-2 rounded mt-1 overflow-auto">
                                    {JSON.stringify(step.details, null, 2)}
                                  </pre>
                                </details>
                              )}
                            </div>
                            <div className="text-xs text-gray-400">
                              Session: {step.session_id?.substring(0, 8)}...
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>

            <TabsContent value="test">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Test Email:</label>
                  <Input
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="test-auth@example.com"
                    className="mt-1"
                  />
                </div>
                
                <Button onClick={testAuthFlow} className="w-full">
                  <Mail className="h-4 w-4 mr-2" />
                  Test Auth Flow (Signup)
                </Button>

                <div className="text-xs text-gray-500">
                  This will attempt to sign up the test email and track the entire flow in the verification logs.
                  The test will create entries in both verification sessions and steps tables.
                </div>
              </div>
            </TabsContent>

            <TabsContent value="current">
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Current User</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {currentUser ? (
                      <div className="space-y-2 text-sm">
                        <div><strong>ID:</strong> {currentUser.id}</div>
                        <div><strong>Email:</strong> {currentUser.email}</div>
                        <div><strong>Email Confirmed:</strong> {currentUser.email_confirmed_at || 'Not confirmed'}</div>
                        <div><strong>Created:</strong> {formatTimestamp(currentUser.created_at)}</div>
                        <div><strong>Role:</strong> {currentUser.role}</div>
                      </div>
                    ) : (
                      <div className="text-gray-400">No user logged in</div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Current Session</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {currentSession ? (
                      <div className="space-y-2 text-sm">
                        <div><strong>Token Type:</strong> {currentSession.token_type}</div>
                        <div><strong>Expires At:</strong> {formatTimestamp(new Date(currentSession.expires_at * 1000).toISOString())}</div>
                        <div><strong>Access Token:</strong> {currentSession.access_token ? 'Present' : 'Missing'}</div>
                        <div><strong>Refresh Token:</strong> {currentSession.refresh_token ? 'Present' : 'Missing'}</div>
                      </div>
                    ) : (
                      <div className="text-gray-400">No active session</div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};